<template>
  <v-col
    :class="{
      'pa-0': true,
      fullScreen: true,
      fullScreenMobile: this.$vuetify.breakpoint.mdAndDown
    }"
    cols="12"
  >
    <v-toolbar dense flat class="toolbar">
      <v-toolbar-items>
        <BackButton
          class="back-btn"
          :defaultRouter="{ name: 'DocumentTemplates' }"
        ></BackButton>
      </v-toolbar-items>
      <v-toolbar-title>{{
        $t("document-templates.document-templates")
      }}</v-toolbar-title>
      <v-toolbar-items>
        <v-btn
          text
          color="primary"
          v-on:click="save()"
          :loading="loading"
          :disabled="disabledSaveButton"
        >
          <v-icon left>{{ id ? "mdi-pencil" : "mdi-plus" }}</v-icon>
          {{ $t(`document-templates.${id ? "update" : "create"}`) }}
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>
    <v-breadcrumbs
      class="pt-0 px-4 pb-1 breadcrumbs"
      v-if="breadcrumbs.length"
      :items="breadcrumbs"
      ><template v-slot:divider>
        >
      </template>
    </v-breadcrumbs>
    <v-row class="px-4 py-1">
      <v-col class="py-0" cols="12" lg="6">
        <v-form ref="form">
          <v-row class="elevation-2 ma-0">
            <v-col class="py-0" cols="4">
              <v-text-field
                v-model="documentTemplate.name"
                :label="$t('document-templates.name')"
                :rules="
                  loading ? [] : [rules.required, backEndValidation('name')]
                "
                :loading="loading"
              ></v-text-field>
            </v-col>
            <v-col class="py-0" cols="4">
              <v-autocomplete
                v-model="documentTemplate._type.id"
                :items="types"
                :label="$t('document-templates.type')"
                item-text="name"
                item-value="id"
                :rules="
                  loading ? [] : [rules.required, backEndValidation('type')]
                "
                :loading="loading"
              ></v-autocomplete>
            </v-col>
            <v-col class="py-0" cols="4">
              <v-autocomplete
                v-model="documentTemplate._library.id"
                :items="libraries"
                :label="$t('document-templates.library')"
                item-text="name"
                item-value="id"
                :rules="
                  loading ? [] : [rules.required, backEndValidation('library')]
                "
                :loading="loading"
              ></v-autocomplete>
            </v-col>
          </v-row>
        </v-form>
        <v-divider></v-divider>
        <v-row class="mt-2  ma-0 elevation-2">
          <div class="tabs-container">
            <v-tabs v-model="tabIndex" class="tabs">
              <v-tab>
                {{ $t(`document-templates.content`) }}
              </v-tab>
              <v-tab>
                {{ $t(`document-templates.localization`) }}
              </v-tab>
              <v-tab v-if="id">
                {{ $t(`document-templates.delete`) }}
              </v-tab>
            </v-tabs>
            <v-divider></v-divider>
            <v-tabs-items v-model="tabIndex">
              <v-tab-item>
                <codemirror
                  class="codemirror-item"
                  v-model="documentTemplate.content"
                  :options="options"
                ></codemirror>
              </v-tab-item>
              <v-tab-item>
                <v-col class="py-0">
                  <v-autocomplete
                    v-model="localizationLanguage"
                    item-value="code"
                    item-text="text"
                    :items="localizationList"
                    :label="$t('document-templates.localization')"
                  >
                    <template v-slot:item="data">
                      <v-list-item-content
                        v-text="data.item.name"
                      ></v-list-item-content>
                      <v-progress-circular
                        :rotate="-90"
                        :size="40"
                        :width="2"
                        :value="data.item.percent"
                        :color="
                          { 0: 'gray', 100: 'success' }[data.item.percent] ||
                            'error'
                        "
                        class="autocomplete-item-circular"
                      >
                        {{ data.item.value }}/{{ data.item.total }}
                      </v-progress-circular>
                    </template>
                  </v-autocomplete>
                </v-col>
                <v-divider></v-divider>
                <div class="localization-container">
                  <template v-for="i in localeKeyMap">
                    <v-row class="ma-0" v-bind:key="'row' + i">
                      <template
                        v-if="
                          localization && localization[localizationLanguage]
                        "
                      >
                        <v-col class="pt-2" cols="6"> {{ i }}: </v-col>
                        <v-col class="py-0" cols="6">
                          <v-text-field
                            dense
                            v-model="localization[localizationLanguage][i]"
                            v-bind:key="`${localizationLanguage}-${i}`"
                          ></v-text-field>
                        </v-col>
                      </template>
                    </v-row>
                    <v-divider v-bind:key="'divider' + i"></v-divider>
                  </template>
                </div>
              </v-tab-item>
              <v-tab-item class="px-4" v-if="id">
                <Delete
                  resource="document-templates"
                  dispatch="documentTemplateStore/delete"
                  routing="DocumentTemplates"
                  :id="id"
                ></Delete>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </v-row>
      </v-col>
      <v-col
        cols="12"
        lg="6"
        class="mt-5 mt-lg-0 pa-0 document-container elevation-2"
      >
        <FieldsEditor
          v-if="documentTemplate && renderer"
          v-model="pseudoDocument"
          :template="documentTemplate"
          :locale="localizationLanguage"
          :localization="targetLocalization"
          :custom-renderer="renderer"
        />
      </v-col>
    </v-row>
  </v-col>
</template>
<script>
import { rules } from "./../components/mixins/rules";
import { permissions } from "./../components/mixins/permissions";
import { title } from "../components/mixins/title";
import { codemirror } from "vue-codemirror";
import Renderer from "../plugins/noDataRenderer";
import Delete from "../components/common/tabs/Delete";
import FieldsEditor from "../components/documents/FieldsEditor";
import BackButton from "../components/common/buttons/BackButton";
export default {
  name: "DocumentTemplate",
  components: { Delete, FieldsEditor, codemirror, BackButton },
  mixins: [title, rules, permissions],
  props: ["id"],
  data: () => ({
    tabIndex: 0,
    options: {
      lineNumbers: true,
      foldGutter: true,
      gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"]
    },
    localizationLanguage: "de-CH",
    localization: {},
    loading: false,
    pseudoDocument: {},
    documentTemplateDefault: {
      _type: { id: "" },
      _library: { id: "" },
      content:
        "<html>\n<head>\n <style></style>\n</head>\n<body>\n   \n</body>\n</html>",
      localization: {}
    }
  }),
  computed: {
    disabledSaveButton() {
      if (this.id) {
        return !this.hasPermissions(
          this.documentTemplate,
          "document_template.update"
        );
      }
      return false;
    },
    renderer: function() {
      return new Renderer();
    },
    localeKeyMap: function() {
      const template = this.documentTemplate.content;
      const locale = this.localizationLanguage;
      this.renderer.render(template, {}, { locale });
      return Array.from(this.renderer.localizationKeys);
    },
    targetLocalization: function() {
      return this.localization?.[this.localizationLanguage];
    },
    documentTemplate: {
      get() {
        if (this.$route.params.id) {
          let documentTemplate = this.$store.getters[
            "documentTemplateStore/getItem"
          ];
          if (documentTemplate && documentTemplate.id) {
            return documentTemplate;
          }
        }
        return this.documentTemplateDefault;
      }
    },
    types: {
      get() {
        return this.$store.getters["documentTypeStore/getList"];
      }
    },
    libraries: {
      get() {
        return this.$store.getters["templateLibraryStore/getList"];
      }
    },
    documentType() {
      if (this.documentTemplate && this.documentTemplate._type && this.types) {
        let id = this.documentTemplate._type.id;
        return this.types.find(t => t.id === id);
      }
      return undefined;
    },
    locales: {
      get() {
        return this.$store.getters["localizationStore/localeStore/getList"];
      }
    },
    localizationList() {
      let localization = this.localization;
      let localeKeyMap = this.localeKeyMap;
      let total = Object.values(localeKeyMap).length;
      let localizationList = this.locales
        .map(item => {
          let currentLocalization = localization[item.code];
          item.value =
            (currentLocalization &&
              Object.values(this.clearSelectedLocalization(currentLocalization))
                .length) ||
            0;
          item.text = `${item.name}, ${item.value}/${total}`;
          item.total = total;
          item.percent = ~~((item.value / item.total) * 100);
          return item;
        })
        .sort(function(a, b) {
          if (a.value > b.value) {
            return -1;
          }
          if (a.value < b.value) {
            return 1;
          }
          return 0;
        });
      let indexForDivider = localizationList.findIndex(i => !i.value);
      if (indexForDivider > 0) {
        localizationList.splice(
          indexForDivider,
          0,
          { header: this.$t("document-templates.unfilled-translations") },
          { divider: true }
        );
      }
      return localizationList;
    },
    breadcrumbs() {
      return this.documentTemplate.id
        ? [
            {
              text: this.$t("document-templates.document-templates"),
              to: {
                name: "DocumentTemplates"
              },
              exact: true
            },
            ...(this.documentTemplate._library &&
            this.documentTemplate._library.id
              ? [
                  {
                    text: this.documentTemplate._library.name,
                    to: {
                      name: "TemplateLibrary",
                      params: {
                        id: this.documentTemplate._library.id
                      }
                    },
                    exact: true
                  }
                ]
              : []),
            {
              text: this.documentTemplate.name,
              to: {
                name: "DocumentTemplate",
                params: {
                  id: this.documentTemplate.id
                }
              },
              exact: true
            }
          ]
        : [];
    }
  },
  watch: {
    localizationLanguage() {
      this.refreshCurrentLocation();
    },
    documentTemplate() {
      if (this.documentTemplate && this.documentTemplate.localization) {
        this.localization = JSON.parse(
          JSON.stringify(this.documentTemplate.localization)
        );
        if (Array.isArray(this.localization)) {
          this.localization = {};
        }
        this.localizationLanguage = Object.keys(
          this.documentTemplate.localization
        )[0];
      } else {
        this.localizationLanguage = "";
      }
    }
  },
  methods: {
    getArgumentForTitle() {
      return {
        chain: [
          this.$t("document-templates.document-templates"),
          this.documentTemplate.name
        ]
      };
    },
    refreshCurrentLocation() {
      this.$set(this.localization, this.localizationLanguage, {
        ...Object.fromEntries(this.localeKeyMap.map(i => [i, ""])),
        ...this.localization[this.localizationLanguage]
      });
    },
    clearSelectedLocalization(localization) {
      let localeKeyMap = this.localeKeyMap;
      return Object.fromEntries(
        Object.entries(localization).filter(
          u => !!u[1] && localeKeyMap.indexOf(u[0]) >= 0
        )
      );
    },
    save() {
      this.clearBackEndErrors();
      if (this.$refs.form.validate()) {
        this.loading = true;

        //clear empty trash stuff in localization
        let localization = JSON.parse(JSON.stringify(this.localization));

        for (let key in localization) {
          if (localization.hasOwnProperty(key)) {
            localization[key] = this.clearSelectedLocalization(
              localization[key]
            );
            if (
              !localization[key] ||
              !(typeof localization[key] === "object") ||
              Object.keys(localization[key]).length === 0
            )
              delete localization[key];
          }
        }
        localization = JSON.stringify(localization);

        this.$store
          .dispatch(
            `documentTemplateStore/${this.$props.id ? "update" : "create"}`,
            {
              item: {
                ...this.documentTemplate,
                _document_type: this.documentTemplate._type,
                _template_library: this.documentTemplate._library,
                localization
              },
              errorHandler: this.errorHandler.bind(this, () => {
                this.$refs.form.validate();
              })
            }
          )
          .then(res => {
            let item = res.data;
            if (item) {
              this.$store.commit("documentTemplateStore/set", {
                item
              });
              this.$router.push({
                name: "DocumentTemplate",
                params: {
                  id: item.id
                }
              });
            }
          })
          .finally(() => (this.loading = false));
      }
    }
  },
  mounted() {
    this.loading = true;
    this.$store.dispatch("documentTypeStore/getList", {
      params: { limit: -1 }
    });
    this.$store.dispatch("templateLibraryStore/getList", {
      params: { limit: -1 }
    });

    if (this.$route.params.id) {
      this.$store
        .dispatch("documentTemplateStore/get", {
          id: this.$route.params.id,
          onError: res => {
            // if system can't find folder redirect user to Contacts page
            if (res.response.status === 404) {
              this.$router.push(this.defaultRouter);
            }
          }
        })
        .finally(() => {
          this.loading = false;
        });
    } else {
      this.loading = false;
    }
  }
};
</script>
<style lang="scss" scoped>
.breadcrumbs::v-deep {
  li:nth-child(even) {
    padding: 0 1ex;
  }
  a:hover {
    text-decoration: underline;
  }
}
.back-btn {
  min-width: 0 !important;
}
.toolbar::v-deep .v-toolbar__content {
  padding-left: 0 !important;
}
.autocomplete-item-circular {
  font-size: 12px;
  font-weight: 600;
}
.tabs-container {
  width: 100%;
}
.codemirror-item {
  height: calc(100vh - 270px);
  &::v-deep .CodeMirror {
    height: 100%;
  }
}
.document-container {
  max-width: 21cm;
  &::v-deep > .list {
    height: calc(100vh - 142px);
    padding: 0 !important;
    margin: 0;
    font-size: 0;
    display: block;
    box-shadow: none;
  }
}
div .localization-container {
  height: calc(100vh - 350px);
  overflow-y: scroll;
  padding: 0;
  margin: 0;
  display: block;
}
</style>
